// Pure (tiptap-free) hashtag helpers + document-walking utilities, extracted from
// Hashtag.ts so the main barrel can re-export them without dragging @tiptap/core into
// the eager bundle. The tiptap extension creator (`createHashtagExtension`) stays in
// Hashtag.ts.
import type { MentionSuggestionConfig, TiptapJsonNode } from "./UserMentionUtils";

export type { TiptapJsonNode } from "./UserMentionUtils";

export const HASHTAG_NODE_TYPE = "hashtag";
// The alphabet is Unicode letters, digits and underscore (board settle round 7 D8, 2026-09-16; it was
// ASCII-only until then, so no non-Latin locale could write a hashtag). A tag still needs at least one
// letter, so `#2026` stays plain text. Combining marks (\p{M}) are admitted as part of the word: the
// vowel signs of Devanagari, Bengali and Thai are marks, not letters, and without them `#भारत` was cut
// to the tag `भ` followed by stray text. Keep this alphabet in step with the backend tag normaliser.
const HASHTAG_TOKEN_REGEX = /(^|[\s(\["'“‘])#([\p{L}\p{M}\p{N}_]*\p{L}[\p{L}\p{M}\p{N}_]*)(?=$|[^\p{L}\p{M}\p{N}_-])/gu;
const HASHTAG_ALLOWED_PATTERN = /^[\p{L}\p{M}\p{N}_]*\p{L}[\p{L}\p{M}\p{N}_]*$/u;

export interface HashtagAttrs {
    tag: string;
    label?: string;
}

export interface HashtagSuggestionItem {
    tag: string;
    label?: string;
    description?: string;
    isCreateAction?: boolean;
}

export interface HashtagSuggestionConfig {
    char?: string;
    allowSpaces?: boolean;
    items?: (props: { query: string; editor?: unknown }) => HashtagSuggestionItem[] | Promise<HashtagSuggestionItem[]>;
    command?: (props: { editor: any; range: any; props: HashtagSuggestionItem }) => void;
    render?: () => {
        onStart?: (props: any) => void;
        onUpdate?: (props: any) => void;
        onKeyDown?: (props: any) => boolean;
        onExit?: () => void;
    };
    allow?: MentionSuggestionConfig["allow"];
}

export function normalizeHashtag(value: unknown): string {
    if (typeof value !== "string") {
        return "";
    }

    return value.trim().replace(/^#+/, "").toLowerCase();
}

export function isValidHashtag(value: unknown): boolean {
    const normalized = normalizeHashtag(value);
    return normalized.length > 0 && normalized.length <= 200 && HASHTAG_ALLOWED_PATTERN.test(normalized);
}

function normalizeHashtagLabel(value: unknown): string | undefined {
    if (typeof value !== "string") {
        return undefined;
    }

    const trimmed = value.trim().replace(/^#+/, "").toLowerCase();
    return trimmed ? trimmed : undefined;
}

function extractCandidateTagsFromText(text: string): string[] {
    const tags: string[] = [];
    let match: RegExpExecArray | null;
    HASHTAG_TOKEN_REGEX.lastIndex = 0;

    while ((match = HASHTAG_TOKEN_REGEX.exec(text)) !== null) {
        const normalized = normalizeHashtag(match[2]);
        if (isValidHashtag(normalized)) {
            tags.push(normalized);
        }
    }

    HASHTAG_TOKEN_REGEX.lastIndex = 0;
    return tags;
}

function isExcludedHashtagContext(node: TiptapJsonNode, ancestorTypes: string[]): boolean {
    if (ancestorTypes.includes("codeBlock")) {
        return true;
    }

    const marks = Array.isArray(node.marks) ? node.marks : [];
    return marks.some((mark) => mark?.type === "code" || mark?.type === "link");
}

function createHashtagNode(tag: string, label?: string): TiptapJsonNode {
    return {
        type: HASHTAG_NODE_TYPE,
        attrs: {
            tag,
            label: normalizeHashtagLabel(label) ?? tag,
        },
    };
}

function splitTextNodeIntoHashtagNodes(node: TiptapJsonNode): TiptapJsonNode[] {
    const text = typeof node.text === "string" ? node.text : "";
    if (!text) {
        return [node];
    }

    const replacements: TiptapJsonNode[] = [];
    let cursor = 0;
    let match: RegExpExecArray | null;
    HASHTAG_TOKEN_REGEX.lastIndex = 0;

    while ((match = HASHTAG_TOKEN_REGEX.exec(text)) !== null) {
        const prefix = match[1] ?? "";
        const rawTag = match[2] ?? "";
        const normalizedTag = normalizeHashtag(rawTag);
        if (!isValidHashtag(normalizedTag)) {
            continue;
        }

        const matchStart = match.index;
        const hashtagStart = matchStart + prefix.length;
        const hashtagEnd = hashtagStart + rawTag.length + 1;

        if (hashtagStart > cursor) {
            replacements.push({
                ...node,
                text: text.slice(cursor, hashtagStart),
            });
        }

        replacements.push(createHashtagNode(normalizedTag, rawTag));
        cursor = hashtagEnd;
    }

    HASHTAG_TOKEN_REGEX.lastIndex = 0;

    if (replacements.length === 0) {
        return [node];
    }

    if (cursor < text.length) {
        replacements.push({
            ...node,
            text: text.slice(cursor),
        });
    }

    return replacements.filter((entry) => entry.type !== "text" || typeof entry.text !== "string" || entry.text.length > 0);
}

function normalizeNodeList(
    nodes: TiptapJsonNode[] | null | undefined,
    ancestorTypes: string[] = [],
): { nodes: TiptapJsonNode[]; changed: boolean } {
    if (!Array.isArray(nodes) || nodes.length === 0) {
        return { nodes: Array.isArray(nodes) ? nodes : [], changed: false };
    }

    const normalizedNodes: TiptapJsonNode[] = [];
    let changed = false;

    for (const node of nodes) {
        if (!node) {
            continue;
        }

        const normalized = normalizeHashtagNode(node, ancestorTypes);
        normalizedNodes.push(...normalized.nodes);
        changed = changed || normalized.changed;
    }

    return { nodes: normalizedNodes, changed };
}

function normalizeHashtagNode(
    node: TiptapJsonNode,
    ancestorTypes: string[] = [],
): { nodes: TiptapJsonNode[]; changed: boolean } {
    if (node.type === HASHTAG_NODE_TYPE) {
        const hashtag = extractHashtagAttrs(node);
        if (!hashtag) {
            // Fall back to the RAW attrs, not to getHashtagDisplayText. That helper short-circuits on
            // `hashtag?.tag` and therefore returns the literal "#" for exactly the attrs that just
            // failed validation — it is written for the valid case. Using it here meant a node carrying
            // `tag: "2024"` or `tag: "日本"` (anything the ASCII-only pattern rejects) was rewritten to a
            // lone "#" and the author's word was gone. TipTapParser.parse runs this normalization on
            // every publish, so the loss was persisted. Fixed 2026-08-04.
            return {
                nodes: [{ type: "text", text: rawHashtagText(node.attrs) }],
                changed: true,
            };
        }

        const normalizedNode = createHashtagNode(hashtag.tag, hashtag.label);
        // Compare the canonical attrs directly (tag/label) rather than JSON.stringify, whose
        // key-order sensitivity produced false "changed" results and forced needless doc rewrites.
        const currentAttrs = (node.attrs ?? {}) as Record<string, unknown>;
        const normalizedAttrs = (normalizedNode.attrs ?? {}) as Record<string, unknown>;
        const changed =
            currentAttrs.tag !== normalizedAttrs.tag || currentAttrs.label !== normalizedAttrs.label;
        return { nodes: [{ ...node, attrs: normalizedNode.attrs }], changed };
    }

    if (node.type === "text" && typeof node.text === "string" && !isExcludedHashtagContext(node, ancestorTypes)) {
        const splitNodes = splitTextNodeIntoHashtagNodes(node);
        return { nodes: splitNodes, changed: splitNodes.length !== 1 || splitNodes[0] !== node };
    }

    if (!Array.isArray(node.content)) {
        return { nodes: [node], changed: false };
    }

    const nextAncestors = node.type ? [...ancestorTypes, node.type] : ancestorTypes;
    const normalizedChildren = normalizeNodeList(node.content, nextAncestors);
    if (!normalizedChildren.changed) {
        return { nodes: [node], changed: false };
    }

    return {
        nodes: [{
            ...node,
            content: normalizedChildren.nodes,
        }],
        changed: true,
    };
}

function visitHashtags(
    node: TiptapJsonNode | null | undefined,
    visit: (node: TiptapJsonNode, ancestorTypes: string[]) => void,
    ancestorTypes: string[] = [],
): void {
    if (!node) {
        return;
    }

    visit(node, ancestorTypes);

    if (!Array.isArray(node.content)) {
        return;
    }

    const nextAncestors = node.type ? [...ancestorTypes, node.type] : ancestorTypes;
    for (const child of node.content) {
        visitHashtags(child, visit, nextAncestors);
    }
}

export function extractHashtagAttrsFromRecord(attrs?: Record<string, unknown> | null): HashtagAttrs | null {
    if (!attrs) {
        return null;
    }

    const tag = normalizeHashtag(attrs.tag ?? attrs.label);
    if (!isValidHashtag(tag)) {
        return null;
    }

    return {
        tag,
        label: normalizeHashtagLabel(attrs.label) ?? tag,
    };
}

export function extractHashtagAttrs(node: TiptapJsonNode | null | undefined): HashtagAttrs | null {
    if (!node || node.type !== HASHTAG_NODE_TYPE) {
        return null;
    }

    return extractHashtagAttrsFromRecord(node.attrs);
}

/**
 * Adds the hashtags ONE node contributes to {@code into}: a hashtag node's tag, or the candidate tags in a
 * text node outside a code block / code mark / link. {@code ancestorTypes} are the node's ancestors' types,
 * root first. Exported so {@code getTipTapParseMetadata} can collect hashtags inside its single walk
 * (C-F13); {@link extractHashtags} is the standalone walk over the same function.
 */
export function collectHashtagsFromNode(node: TiptapJsonNode, ancestorTypes: string[], into: Set<string>): void {
    const hashtag = extractHashtagAttrs(node);
    if (hashtag) {
        into.add(hashtag.tag);
        return;
    }

    if (node.type === "text" && typeof node.text === "string" && !isExcludedHashtagContext(node, ancestorTypes)) {
        for (const candidate of extractCandidateTagsFromText(node.text)) {
            into.add(candidate);
        }
    }
}

export function extractHashtags(doc: TiptapJsonNode | null | undefined): string[] {
    const tags = new Set<string>();
    visitHashtags(doc, (node, ancestorTypes) => collectHashtagsFromNode(node, ancestorTypes, tags));
    return [...tags];
}

export function countHashtags(doc: TiptapJsonNode | null | undefined): number {
    let count = 0;

    visitHashtags(doc, (node, ancestorTypes) => {
        if (extractHashtagAttrs(node)) {
            count += 1;
            return;
        }

        if (node.type === "text" && typeof node.text === "string" && !isExcludedHashtagContext(node, ancestorTypes)) {
            count += extractCandidateTagsFromText(node.text).length;
        }
    });

    return count;
}

export function getHashtagDisplayText(attrs?: Record<string, unknown> | null): string {
    const hashtag = extractHashtagAttrsFromRecord(attrs);
    return hashtag?.tag ? `#${hashtag.label ?? hashtag.tag}` : "#";
}

/**
 * The author's original text for a hashtag node whose attrs did NOT validate.
 *
 * Deliberately separate from {@link getHashtagDisplayText}, which is for the valid case and collapses to
 * a bare "#" precisely when validation failed. This one reads the raw attrs so a rejected tag degrades to
 * the word the author typed rather than to punctuation. Returns "#" only when there is genuinely nothing
 * to recover.
 */
function rawHashtagText(attrs?: Record<string, unknown> | null): string {
    const raw = attrs?.["label"] ?? attrs?.["tag"];
    const text = typeof raw === "string" ? raw.trim() : "";
    return text ? `#${text}` : "#";
}

export function normalizeHashtagItem(item: HashtagSuggestionItem): HashtagSuggestionItem {
    const tag = normalizeHashtag(item?.tag ?? item?.label);
    const label = normalizeHashtagLabel(item?.label) ?? tag;

    return {
        ...item,
        tag,
        label,
    };
}

export function normalizeHashtagsInDocument(doc: TiptapJsonNode | null | undefined): TiptapJsonNode | null | undefined {
    if (!doc) {
        return doc;
    }

    const normalized = normalizeHashtagNode(doc);
    return normalized.nodes[0] ?? doc;
}
